import { createSlice } from "@reduxjs/toolkit";
import { clearAuthStorage, setCompanyFlowStatus } from "../utils/common";
import { api } from "./api";
const sidebarClose = '80px';
const sidebarOpen = '255px';

// localStorage stays the persistence layer (it survives a reload, and the
// helpers in utils/common.js read it), but the store is the source of truth for
// "is there a session". Reading the token straight out of localStorage at
// request time meant nothing in React depended on it: a query that fired before
// the token was written went out with no Authorization header and was never
// retried, which is why the header/sidebar stayed empty until a hard refresh.
const readStorage = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

// Only the three fields the app actually uses are persisted, matching what the
// login paths have always written.
const toSessionUser = (user) =>
  user ? { id: user.id, roleId: user.roleId, userType: user.userType } : null;

const readPersistedUser = () => {
  try {
    const raw = readStorage("user");
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const persistCredentials = (token, sessionUser) => {
  try {
    if (sessionUser) {
      localStorage.setItem("user", JSON.stringify(sessionUser));
      localStorage.setItem("type", sessionUser.userType ?? "");
    }
    if (token) localStorage.setItem("token", token);
  } catch (e) {
    /* storage unavailable (private mode) */
  }
};

const initialState = {
  sidebar: false,
  sidebarWidth: sidebarClose,
  token: readStorage('token') || null,
  user: readPersistedUser(),
  permissions: {},
  fromSidebarButton: false,
};
export const appSlice = createSlice({
  name: "app",
  initialState,
  reducers: {
    setSidebar: (state, action) => {
      state.sidebar = !state.sidebar;
      state.sidebar ? state.sidebarWidth = sidebarOpen : state.sidebarWidth = sidebarClose;
      state.fromSidebarButton = action?.payload?.fromSidebarButton
    },
    setPermission: (state, action) => {
      state.permissions = action.payload;
    },
    // Called by every path that completes a sign-in, before it navigates. The
    // caller should also dispatch `api.util.resetApiState()` so nothing cached
    // while logged out — a 401 included — survives into the new session.
    setCredentials: (state, action) => {
      const { token, user } = action.payload || {};
      const sessionUser = toSessionUser(user);
      state.token = token || null;
      state.user = sessionUser;
      state.permissions = {};
      persistCredentials(token, sessionUser);
      setCompanyFlowStatus(user?.showCompanyFlow || 0);
    },
    clearCredentials: (state) => {
      state.token = null;
      state.user = null;
      state.permissions = {};
      clearAuthStorage();
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      api.endpoints.login.matchFulfilled,
      (state, { payload }) => {
        if (payload?.status === "FAILURE") return;
        // Two-factor step 1: the token is only a temp token for otp-verify, and
        // must not become the session. It lives in sessionStorage until then.
        if (payload?.result?.otp === true) {
          try {
            sessionStorage.setItem("jwttoken", payload.result.jwtToken)
          } catch (e) { /* storage unavailable */ }
          return;
        }
        const user = payload?.result?.userData;
        const sessionUser = toSessionUser(user);
        state.token = payload?.result?.jwtToken || null;
        state.user = sessionUser;
        state.permissions = {};
        persistCredentials(payload?.result?.jwtToken, sessionUser);
        setCompanyFlowStatus(user?.showCompanyFlow || 0)
      }
    )
  }
});
export default appSlice.reducer;

export const { setSidebar, setPermission, setCredentials, clearCredentials } = appSlice.actions;
